import { graphql } from 'babel-plugin-relay/macro'
import React, { useCallback, useEffect } from 'react'
import { useSnackbar } from 'notistack'
import { fetchQuery } from 'react-relay'
import { useLazyLoadQuery } from 'react-relay/hooks'
import { useLocation } from 'react-router'
import { removeIsTokenShared } from '../api/server'
import NoAccess from '../components/NoAccess'
import NoWorkspace from '../components/NoWorkspace'
import useWorkspace from '../hooks/useWorkspace'
import environment from '../relayEnvironment'
import SessionContext from '../SessionContext'
import { LayoutOrchestratorSessionQuery } from '../__generated__/LayoutOrchestratorSessionQuery.graphql'
import External from './External'
import Internal from './Internal'
import Public from './Public'

const LayoutOrchestrator: React.FC = () => {
  const data = useLazyLoadQuery<LayoutOrchestratorSessionQuery>(query, {})
  const roles = data?.sessionMe?.contact?.displayRoles
  const isLoggedIn = data.sessionMe !== null
  const location = useLocation()
  const { enqueueSnackbar } = useSnackbar()

  // The new frontend bounces the user back here with `?unauthorized=true` when
  // its shared token has expired. Delete the `isTokenShared` marker so a fresh
  // token is re-shared on the next V3/Reports click, tell the user, then strip
  // the flag from the URL so a refresh doesn't repeat the message.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('unauthorized') === 'true') {
      removeIsTokenShared()
      enqueueSnackbar('Your session has expired. Please sign in again.', { variant: 'warning' })
      params.delete('unauthorized')
      const search = params.toString()
      window.history.replaceState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}`)
    }
  }, [enqueueSnackbar])

  const refresh = useCallback(() => {
    fetchQuery(environment, query, {})
  }, [])

  const [currentWorksapce] = useWorkspace()
  if (location.pathname === '/' && data?.sessionMe?.defaultWorkspace) {
    // Preserve any query string (e.g. ?unauthorized=true) across the workspace
    // redirect so the effect above can still process it on the next load.
    window.location.href = `/${data?.sessionMe?.defaultWorkspace}${window.location.search}`
    return <p>Loading {data?.sessionMe?.defaultWorkspace}...</p>
  }

  return (
    <SessionContext.Provider
      value={{
        user: data.sessionMe,
        isLoggedIn: isLoggedIn,
        reloadSession: refresh,
      }}
    >
      {isLoggedIn ? (
        data && data.sessionMe && data.sessionMe.allowedWorkspaces.includes(currentWorksapce) ? (
          data.sessionMe.contact?.contactType === 'internal' ? (
            <Internal />
          ) : (
            <External roles={roles} />
          )
        ) : currentWorksapce && currentWorksapce.length >= 1 ? (
          <NoWorkspace />
        ) : (
          <>
            <NoAccess />
          </>
        )
      ) : (
        <Public />
      )}
    </SessionContext.Provider>
  )
}

const query = graphql`
  query LayoutOrchestratorSessionQuery {
    sessionMe {
      username
      contact {
        slug
        name
        workspace
        contactType
        displayRoles {
          name
          value
        }
        profileUrl
        email
        position
      }
      defaultWorkspace
      allowedWorkspaces
      registeredInvitedContacts {
        name
        workspace
        email
      }
    }
  }
`

export default LayoutOrchestrator
